/**
 * Passive processing for "notifications:replay" (historical backlog on
 * mount/reconnect), kept separate from the live "notifications" path so the
 * replay can never trigger the per-notification domain side effects
 * (operation/batch fetch+dispatch, protocol reload, sound, toast) that cause
 * fan-out. `processReplayNotifications` only accepts `add` and a pure text
 * resolver (`buildMessage`) - it has no access to axios/dispatch, so it
 * cannot call any business fetcher.
 */

const REPLAY_LINKABLE_TYPES = [
  "NOTE",
  "NEW_OPERATION",
  "UPDATE_OPERATION",
  "START_OPERATION",
  "DELETE_OPERATION",
  "COMPLETE_SAMPLING",
];

/**
 * Pure: derive the inbox-display fields for a replayed notification row.
 * No fetch/dispatch/sound/toast - just title/content/date/link.
 *
 * `buildMessage` (typically buildNotificationMessage bound to getLabel/
 * configs/role) resolves the human-readable text for the notification's
 * technical `type`; the raw type is only ever shown as the last-resort
 * fallback, for legacy payloads with neither a backend-provided `content`
 * nor a type the resolver recognizes.
 */
export const buildReplayNotificationDisplay = (raw, { buildMessage } = {}) => {
  const operationId = raw?.data?.operation_id;
  const operationType = raw?.data?.operation_type;
  const isLinkable = Boolean(
    operationId && operationType && REPLAY_LINKABLE_TYPES.includes(raw?.type)
  );

  return {
    id: raw?.id,
    title: raw?.title ?? "",
    content: raw?.content || buildMessage?.(raw) || raw?.type || "",
    // Original timestamp always wins; receipt-time is only a last-resort
    // fallback for legacy payloads missing both created_at and date.
    date: raw?.created_at || raw?.date || new Date().toISOString(),
    link: isLinkable
      ? `/programming/${String(operationType).toLowerCase()}/${operationId}`
      : null,
  };
};

/**
 * Filter a replay batch to the active winery, dedupe by id against `seenIds`
 * (shared with the live path so a notification seen on either channel is
 * never added twice), and add each surviving one to the inbox via `add`.
 * Returns the number of notifications added.
 */
export const processReplayNotifications = (
  dataArray,
  { activeWineryId, seenIds, add, buildMessage }
) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) return 0;

  const forActiveWinery = dataArray.filter((raw) => {
    const wineryId = raw?.winery_id ?? raw?.data?.winery_id;
    return Number(wineryId) === Number(activeWineryId);
  });

  let added = 0;
  forActiveWinery.forEach((raw) => {
    if (raw?.id != null) {
      if (seenIds.has(raw.id)) return;
      seenIds.add(raw.id);
    }

    const display = buildReplayNotificationDisplay(raw, { buildMessage });

    add({
      id: display.id ?? raw?.id ?? `notif_replay_${Date.now()}_${added}`,
      title: display.title,
      content: display.content,
      date: display.date,
      data: { ...raw?.data, exclude: false },
    });
    added += 1;
  });

  return added;
};
